import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, map, timer, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../core/types/api.types';

export type NotifKind =
  | 'COMMANDE'
  | 'FACTURE'
  | 'PAIEMENT'
  | 'STOCK'
  | 'SYSTEME'
  | 'CLIENT'
  | 'ABONNEMENT';

export interface AppNotification {
  reference: string;
  type: NotifKind;
  titre: string;
  message: string;
  lien?: string;
  lue: boolean;
  importante?: boolean;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationCenterService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/notifications`;

  private _items$ = new BehaviorSubject<AppNotification[]>([]);
  private _unread$ = new BehaviorSubject<number>(0);
  private pollingSubscription: Subscription | null = null;

  get items$(): Observable<AppNotification[]> {
    return this._items$.asObservable();
  }
  get unreadCount$(): Observable<number> {
    return this._unread$.asObservable();
  }

  /** Démarre le polling toutes les 30s (à appeler une fois au login). Stoppe le polling précédent si actif. */
  startPolling(): void {
    this.stopPolling();
    this.pollingSubscription = timer(0, 30_000)
      .pipe(switchMap(() => this.fetchInternal()))
      .subscribe();
  }

  /** Arrête le polling (à appeler au logout). */
  stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  fetch(): Observable<AppNotification[]> {
    return this.fetchInternal();
  }

  private fetchInternal(): Observable<AppNotification[]> {
    return this.http
      .get<ApiResponse<AppNotification[]>>(this.base)
      .pipe(
        map((r) => r.data || []),
        map((items) => {
          this._items$.next(items);
          this._unread$.next(items.filter((i) => !i.lue).length);
          return items;
        }),
      );
  }

  markRead(ref: string): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.base}/${ref}/read`, {})
      .pipe(
        map(() => {
          const items = this._items$.value.map((i) =>
            i.reference === ref ? { ...i, lue: true } : i,
          );
          this._items$.next(items);
          this._unread$.next(items.filter((i) => !i.lue).length);
        }),
      );
  }

  markAllRead(): Observable<void> {
    return this.http
      .post<ApiResponse<void>>(`${this.base}/read-all`, {})
      .pipe(
        map(() => {
          const items = this._items$.value.map((i) => ({ ...i, lue: true }));
          this._items$.next(items);
          this._unread$.next(0);
        }),
      );
  }

  delete(ref: string): Observable<void> {
    return this.http
      .delete<ApiResponse<void>>(`${this.base}/${ref}`)
      .pipe(
        map(() => {
          const items = this._items$.value.filter((i) => i.reference !== ref);
          this._items$.next(items);
          this._unread$.next(items.filter((i) => !i.lue).length);
        }),
      );
  }
}

export const NOTIF_KIND_ICONS: Record<string, string> = {
  COMMANDE: 'package',
  FACTURE: 'file-invoice',
  PAIEMENT: 'cash',
  STOCK: 'box',
  SYSTEME: 'settings',
  CLIENT: 'user',
  ABONNEMENT: 'crown',
};

export const NOTIF_KIND_COLORS: Record<string, string> = {
  COMMANDE: 'text-primary bg-primary/10',
  FACTURE: 'text-info bg-info/10',
  PAIEMENT: 'text-success bg-success/10',
  STOCK: 'text-warning bg-warning/10',
  SYSTEME: 'text-muted bg-muted/20',
  CLIENT: 'text-primary bg-primary/10',
  ABONNEMENT: 'text-warning bg-warning/10',
};
